import { motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Settings,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { formatExerciseTitle } from '../utils/formatTitle';
import { getApiUrl } from '../utils/apiUrl';

interface SyncLog {
  id: string;
  exercise_id: string;
  sync_type: 'manual' | 'scheduled' | 'webhook';
  sync_frequency: string | null;
  status: 'success' | 'failed' | 'skipped';
  version_created: string | null;
  github_commit_hash: string | null;
  error_message: string | null;
  started_at: string;
  completed_at: string;
  duration_ms: number;
  exercises?: {
    id: string;
    title: string;
    category: string;
  };
  exercise_sources?: {
    id: string;
    github_repo_url: string;
    github_repo_path: string;
    sync_frequency: string;
  };
}

interface SyncStatistics {
  total_syncs: number;
  successful_syncs: number;
  failed_syncs: number;
  skipped_syncs: number;
  avg_duration_ms: number;
  last_sync_at: string | null;
}

interface ExerciseSyncStatus {
  exercise_id: string;
  github_repo_url: string;
  github_repo_path: string;
  sync_enabled: boolean;
  sync_frequency: string;
  last_sync_at: string | null;
  exercises: {
    id: string;
    title: string;
    category: string;
  };
  latest_sync: {
    status: string;
    completed_at: string;
    version_created: string | null;
  } | null;
}

const SyncDashboard: React.FC = () => {
  const { getAuthToken } = useAuth();
  const [statistics, setStatistics] = useState<SyncStatistics | null>(null);
  const [exerciseStatuses, setExerciseStatuses] = useState<
    ExerciseSyncStatus[]
  >([]);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [daysBack, setDaysBack] = useState(7);
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string | React.ReactNode;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysBack]);

  const fetchData = async () => {
    try {
      const token = await getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');

      // Fetch statistics
      try {
        const statsResponse = await fetch(
          `${baseUrl}/api/exercise-versions/sync-stats?days=${daysBack}`,
          { headers }
        );
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStatistics(statsData.statistics);
        } else {
          console.error('Failed to fetch sync stats:', statsResponse.status);
          // Set empty stats on error
          setStatistics({
            total_syncs: 0,
            successful_syncs: 0,
            failed_syncs: 0,
            skipped_syncs: 0,
            avg_duration_ms: 0,
            last_sync_at: null,
          });
        }
      } catch (statsError) {
        console.error('Error fetching sync stats:', statsError);
      }

      // Fetch exercise statuses
      try {
        const statusResponse = await fetch(
          `${baseUrl}/api/exercise-versions/sync-status`,
          {
            headers,
          }
        );
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setExerciseStatuses(statusData.exercises || []);
        } else {
          const errorData = await statusResponse.json().catch(() => ({}));
          console.error(
            'Failed to fetch sync status:',
            statusResponse.status,
            errorData
          );
          // Show error modal for sync-status failures
          if (statusResponse.status === 500) {
            setModal({
              isOpen: true,
              title: 'Sync Status Error',
              message: (
                <div className='space-y-3'>
                  <p className='font-semibold text-red-700'>
                    Failed to load exercise sync status
                  </p>
                  <p className='text-sm text-gray-700'>
                    {errorData.error ||
                      'Server error occurred while fetching sync status'}
                  </p>
                  {errorData.troubleshooting && (
                    <div className='bg-white/50 rounded-lg p-4 border border-red-200'>
                      <p className='text-sm font-medium mb-2 text-red-800'>
                        🔧 Troubleshooting:
                      </p>
                      <ul className='text-sm list-disc list-inside space-y-1 text-gray-700'>
                        {errorData.troubleshooting.map(
                          (step: string, idx: number) => (
                            <li key={idx}>{step}</li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                  <p className='text-xs text-gray-600'>
                    Check Vercel function logs for detailed error information
                  </p>
                </div>
              ),
              type: 'error',
            });
          }
          // Set empty array on error
          setExerciseStatuses([]);
        }
      } catch (statusError) {
        console.error('Error fetching sync status:', statusError);
        setExerciseStatuses([]);
      }

      // Fetch recent sync logs
      try {
        const logsResponse = await fetch(
          `${baseUrl}/api/exercise-versions/sync-logs?limit=50`,
          { headers }
        );
        if (logsResponse.ok) {
          const logsData = await logsResponse.json();
          setSyncLogs(logsData.logs || []);
        } else {
          console.error('Failed to fetch sync logs:', logsResponse.status);
          setSyncLogs([]);
        }
      } catch (logsError) {
        console.error('Error fetching sync logs:', logsError);
        setSyncLogs([]);
      }
    } catch (error) {
      console.error('Error fetching sync data:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncExercise = async (exerciseId: string) => {
    try {
      setSyncing(exerciseId);
      const token = await getAuthToken();
      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${baseUrl}/api/exercise-versions/${exerciseId}/sync`,
        {
          method: 'POST',
          headers,
        }
      );

      if (response.ok) {
        // Wait a bit for the sync to complete and log
        setTimeout(() => {
          fetchData();
        }, 2000);
      } else {
        console.error('Sync failed');
      }
    } catch (error) {
      console.error('Error syncing exercise:', error);
    } finally {
      setSyncing(null);
    }
  };

  const syncAll = async () => {
    try {
      setSyncingAll(true);
      const token = await getAuthToken();
      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${baseUrl}/api/exercise-versions/sync-all`,
        {
          method: 'POST',
          headers,
        }
      );

      if (response.ok) {
        // Wait a bit for syncs to complete
        setTimeout(() => {
          fetchData();
        }, 3000);
      }
    } catch (error) {
      console.error('Error syncing all exercises:', error);
    } finally {
      setSyncingAll(false);
    }
  };

  const discoverExercises = async () => {
    try {
      setDiscovering(true);
      const token = await getAuthToken();
      const baseUrl = getApiUrl().replace(/\/api\/?$/, '');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${baseUrl}/api/exercise-versions/discover`,
        {
          method: 'POST',
          headers,
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Show a more informative message
        const message =
          data.message ||
          (data.completed
            ? 'Exercise discovery completed successfully!'
            : 'Exercise discovery started. Check backend terminal for progress.');

        const successMessage = (
          <div className='space-y-3'>
            <p className='font-semibold text-green-700'>{message}</p>

            {/* Show statistics if available */}
            {(data.exercisesCreated !== undefined ||
              data.exercisesSkipped !== undefined ||
              data.totalFound !== undefined) && (
              <div className='bg-white/50 rounded-lg p-4 border border-green-200'>
                <p className='text-sm font-medium mb-2 text-green-800'>
                  📊 Discovery Results:
                </p>
                {data.totalFound !== undefined && (
                  <p className='text-sm'>
                    <strong>Total Files Found:</strong> {data.totalFound}
                  </p>
                )}
                {data.exercisesCreated !== undefined && (
                  <p className='text-sm'>
                    <strong>Exercises Created:</strong>{' '}
                    <span className='text-green-600 font-semibold'>
                      {data.exercisesCreated}
                    </span>
                  </p>
                )}
                {data.exercisesSkipped !== undefined &&
                  data.exercisesSkipped > 0 && (
                    <p className='text-sm'>
                      <strong>Exercises Skipped:</strong>{' '}
                      <span className='text-yellow-600'>
                        {data.exercisesSkipped} (already exist)
                      </span>
                    </p>
                  )}
                {data.configsCreated !== undefined && (
                  <p className='text-sm text-gray-600'>
                    Configs Generated: {data.configsCreated}
                  </p>
                )}
              </div>
            )}

            {/* Show warnings if any */}
            {data.warning && (
              <div className='bg-yellow-50 rounded-lg p-4 border border-yellow-200'>
                <p className='text-sm font-medium mb-1 text-yellow-800'>
                  ⚠️ Warning:
                </p>
                <p className='text-sm text-yellow-700'>{data.warning}</p>
              </div>
            )}

            {/* Show errors if any */}
            {data.errors && data.errors.length > 0 && (
              <div className='bg-red-50 rounded-lg p-4 border border-red-200'>
                <p className='text-sm font-medium mb-2 text-red-800'>
                  ❌ Errors ({data.errors.length}):
                </p>
                <ul className='text-xs list-disc list-inside space-y-1 text-red-700 max-h-40 overflow-y-auto'>
                  {data.errors.slice(0, 5).map((err: any, idx: number) => (
                    <li key={idx}>
                      {err.exercise || 'Unknown'}: {err.error}
                    </li>
                  ))}
                  {data.errors.length > 5 && (
                    <li className='text-gray-600 italic'>
                      ... and {data.errors.length - 5} more errors
                    </li>
                  )}
                </ul>
                <p className='text-xs text-gray-600 mt-2'>
                  Check Vercel function logs for full error details
                </p>
              </div>
            )}

            {/* Show tip for background processing */}
            {!data.completed && (
              <div className='bg-white/50 rounded-lg p-4 border border-green-200'>
                <p className='text-sm font-medium mb-2'>💡 Tip:</p>
                <p className='text-sm'>
                  Discovery is running in the background. Check logs for
                  progress:
                </p>
                <ul className='text-sm list-disc list-inside mt-2 space-y-1'>
                  <li>Vercel: Project → Functions → Logs</li>
                  <li>Supabase: Project → Logs → Postgres Logs</li>
                  <li>Browser: DevTools → Console</li>
                </ul>
              </div>
            )}
          </div>
        );

        setModal({
          isOpen: true,
          title: 'Discovery Started',
          message: successMessage,
          type: 'success',
        });

        // Refresh data after delays to see new exercises as they're created
        setTimeout(() => fetchData(), 10000); // First refresh after 10 seconds
        setTimeout(() => fetchData(), 30000); // Second refresh after 30 seconds
        setTimeout(() => fetchData(), 60000); // Final refresh after 1 minute
      } else {
        const errorData = await response.json().catch(() => ({
          error: 'Failed to parse error response',
        }));

        // Extract error information
        const errorTitle = errorData.error || 'Unknown error';
        const errorMessage =
          errorData.message || errorData.error || 'An unknown error occurred';
        const troubleshooting = errorData.troubleshooting || [];
        const details = errorData.details;
        const suggestion = errorData.suggestion;

        const errorMessageContent = (
          <div className='space-y-3'>
            <div>
              <p className='font-semibold text-red-700 mb-1'>{errorTitle}</p>
              <p className='text-sm text-gray-700'>{errorMessage}</p>
            </div>

            {suggestion && (
              <div className='bg-blue-50 rounded-lg p-3 border border-blue-200'>
                <p className='text-sm font-medium text-blue-800 mb-1'>
                  💡 Suggestion:
                </p>
                <p className='text-sm text-blue-700'>{suggestion}</p>
              </div>
            )}

            {troubleshooting.length > 0 && (
              <div className='bg-white/50 rounded-lg p-4 border border-red-200'>
                <p className='text-sm font-medium mb-2 text-red-800'>
                  🔧 Troubleshooting Steps:
                </p>
                <ul className='text-sm list-disc list-inside space-y-1 text-gray-700'>
                  {troubleshooting.map((step: string, index: number) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>
            )}

            {details && process.env.NODE_ENV === 'development' && (
              <div className='bg-gray-50 rounded-lg p-3 border border-gray-200'>
                <p className='text-xs font-mono text-gray-600 whitespace-pre-wrap break-all'>
                  {details}
                </p>
              </div>
            )}

            <div className='bg-yellow-50 rounded-lg p-3 border border-yellow-200'>
              <p className='text-sm font-medium mb-1 text-yellow-800'>
                📋 Where to Check Logs:
              </p>
              <ul className='text-xs list-disc list-inside space-y-1 text-yellow-700'>
                <li>
                  <strong>Vercel:</strong> Project → Functions → api/index.js →
                  Logs
                </li>
                <li>
                  <strong>Supabase:</strong> Project → Logs → Postgres Logs
                </li>
                <li>
                  <strong>Browser:</strong> DevTools → Console & Network tabs
                </li>
              </ul>
            </div>
          </div>
        );

        setModal({
          isOpen: true,
          title: 'Discovery Failed',
          message: errorMessageContent,
          type: 'error',
        });
      }
    } catch (error: any) {
      console.error('Error discovering exercises:', error);

      const errorMessage = (
        <div className='space-y-3'>
          <p className='font-semibold text-red-700'>
            Network or Connection Error
          </p>
          <p className='text-sm text-gray-700'>
            {error.message ||
              'Failed to connect to the server. This could be a network issue or the server might be unavailable.'}
          </p>
          <div className='bg-white/50 rounded-lg p-4 border border-red-200'>
            <p className='text-sm font-medium mb-2 text-red-800'>
              🔧 Troubleshooting:
            </p>
            <ul className='text-sm list-disc list-inside space-y-1 text-gray-700'>
              <li>Check your internet connection</li>
              <li>
                Verify the API URL is correct:{' '}
                {getApiUrl() || 'http://localhost:3001'}
              </li>
              <li>Check if the backend server is running</li>
              <li>Look for CORS errors in browser console</li>
              <li>Check Vercel deployment status if in production</li>
            </ul>
          </div>
        </div>
      );

      setModal({
        isOpen: true,
        title: 'Discovery Error',
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setDiscovering(false);
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'skipped':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className='w-4 h-4' />;
      case 'failed':
        return <XCircle className='w-4 h-4' />;
      case 'skipped':
        return <AlertCircle className='w-4 h-4' />;
      default:
        return null;
    }
  };

  const filteredLogs = syncLogs.filter(log => {
    if (selectedFilter === 'all') return true;
    return log.status === selectedFilter;
  });

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <RefreshCw className='w-8 h-8 animate-spin text-hexagon' />
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      <div className='min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className='mb-8'
          >
            <div className='flex items-center justify-between'>
              <div>
                <div className='flex items-center space-x-4 mb-2'>
                  <h1 className='text-3xl font-bold text-gray-900'>
                    Sync Dashboard
                  </h1>
                  <Link
                    to='/admin/exercises'
                    className='text-sm text-hexagon hover:text-hexagon-dark font-medium flex items-center space-x-1'
                  >
                    <Settings className='w-4 h-4' />
                    <span>View Version Manager</span>
                  </Link>
                </div>
                <p className='text-gray-600'>
                  Monitor and manage exercise synchronization from GitHub
                </p>
              </div>
              <div className='flex items-center space-x-3'>
                <button
                  onClick={discoverExercises}
                  disabled={discovering}
                  className='hexagon-button flex items-center space-x-2 disabled:opacity-50 bg-green-600 hover:bg-green-700'
                >
                  <Activity
                    className={`w-5 h-5 ${discovering ? 'animate-pulse' : ''}`}
                  />
                  <span>Discover Exercises</span>
                </button>
                <button
                  onClick={syncAll}
                  disabled={syncingAll}
                  className='hexagon-button flex items-center space-x-2 disabled:opacity-50'
                >
                  <RefreshCw
                    className={`w-5 h-5 ${syncingAll ? 'animate-spin' : ''}`}
                  />
                  <span>Sync All Exercises</span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Statistics Cards */}
          {statistics && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'
            >
              <div className='glass-effect rounded-xl p-6'>
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-gray-600 text-sm'>Total Syncs</span>
                  <Activity className='w-5 h-5 text-hexagon' />
                </div>
                <div className='text-3xl font-bold text-gray-900'>
                  {statistics.total_syncs}
                </div>
                <div className='text-sm text-gray-500 mt-1'>
                  Last {daysBack} days
                </div>
              </div>

              <div className='glass-effect rounded-xl p-6'>
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-gray-600 text-sm'>Successful</span>
                  <CheckCircle className='w-5 h-5 text-green-600' />
                </div>
                <div className='text-3xl font-bold text-green-600'>
                  {statistics.successful_syncs}
                </div>
                <div className='text-sm text-gray-500 mt-1'>
                  {statistics.total_syncs > 0
                    ? Math.round(
                        (statistics.successful_syncs / statistics.total_syncs) *
                          100
                      )
                    : 0}
                  % success rate
                </div>
              </div>

              <div className='glass-effect rounded-xl p-6'>
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-gray-600 text-sm'>Failed</span>
                  <XCircle className='w-5 h-5 text-red-600' />
                </div>
                <div className='text-3xl font-bold text-red-600'>
                  {statistics.failed_syncs}
                </div>
                <div className='text-sm text-gray-500 mt-1'>
                  {statistics.skipped_syncs} skipped
                </div>
              </div>

              <div className='glass-effect rounded-xl p-6'>
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-gray-600 text-sm'>Avg Duration</span>
                  <Clock className='w-5 h-5 text-blue-600' />
                </div>
                <div className='text-3xl font-bold text-blue-600'>
                  {statistics.avg_duration_ms
                    ? formatDuration(Number(statistics.avg_duration_ms))
                    : 'N/A'}
                </div>
                <div className='text-sm text-gray-500 mt-1'>
                  Last sync:{' '}
                  {statistics.last_sync_at
                    ? formatDate(statistics.last_sync_at)
                    : 'Never'}
                </div>
              </div>
            </motion.div>
          )}

          {/* Exercise Status Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className='glass-effect rounded-xl p-6 mb-8'
          >
            <h2 className='text-xl font-bold text-gray-900 mb-4'>
              Exercise Sync Status
            </h2>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-gray-200'>
                    <th className='text-left py-3 px-4 text-sm font-medium text-gray-700'>
                      Exercise
                    </th>
                    <th className='text-left py-3 px-4 text-sm font-medium text-gray-700'>
                      Repository
                    </th>
                    <th className='text-left py-3 px-4 text-sm font-medium text-gray-700'>
                      Frequency
                    </th>
                    <th className='text-left py-3 px-4 text-sm font-medium text-gray-700'>
                      Last Sync
                    </th>
                    <th className='text-left py-3 px-4 text-sm font-medium text-gray-700'>
                      Status
                    </th>
                    <th className='text-left py-3 px-4 text-sm font-medium text-gray-700'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {exerciseStatuses.map(exercise => (
                    <tr
                      key={exercise.exercise_id}
                      className='border-b border-gray-100 hover:bg-gray-50'
                    >
                      <td className='py-3 px-4'>
                        <div className='font-medium text-gray-900'>
                          {formatExerciseTitle(
                            exercise.exercises?.title || 'Unknown'
                          )}
                        </div>
                        <div className='text-sm text-gray-500'>
                          {exercise.exercises?.category}
                        </div>
                      </td>
                      <td className='py-3 px-4'>
                        <div className='text-sm text-gray-900'>
                          {exercise.github_repo_url
                            .split('/')
                            .slice(-2)
                            .join('/')}
                        </div>
                        <div className='text-xs text-gray-500'>
                          {exercise.github_repo_path}
                        </div>
                      </td>
                      <td className='py-3 px-4'>
                        <span className='px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800'>
                          {exercise.sync_frequency}
                        </span>
                      </td>
                      <td className='py-3 px-4 text-sm text-gray-600'>
                        {formatDate(exercise.last_sync_at)}
                      </td>
                      <td className='py-3 px-4'>
                        {exercise.latest_sync ? (
                          <span
                            className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              exercise.latest_sync.status
                            )}`}
                          >
                            {getStatusIcon(exercise.latest_sync.status)}
                            <span className='capitalize'>
                              {exercise.latest_sync.status}
                            </span>
                          </span>
                        ) : (
                          <span className='text-xs text-gray-400'>
                            Never synced
                          </span>
                        )}
                      </td>
                      <td className='py-3 px-4'>
                        <button
                          onClick={() => syncExercise(exercise.exercise_id)}
                          disabled={syncing === exercise.exercise_id}
                          className='text-hexagon hover:text-hexagon-dark text-sm font-medium disabled:opacity-50 flex items-center space-x-1'
                        >
                          <RefreshCw
                            className={`w-4 h-4 ${
                              syncing === exercise.exercise_id
                                ? 'animate-spin'
                                : ''
                            }`}
                          />
                          <span>Sync</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Sync Logs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className='glass-effect rounded-xl p-6'
          >
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-xl font-bold text-gray-900'>
                Recent Sync Logs
              </h2>
              <div className='flex items-center space-x-2'>
                <select
                  value={selectedFilter}
                  onChange={e => setSelectedFilter(e.target.value)}
                  aria-label='Filter by status'
                  className='px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hexagon'
                >
                  <option value='all'>All Statuses</option>
                  <option value='success'>Success</option>
                  <option value='failed'>Failed</option>
                  <option value='skipped'>Skipped</option>
                </select>
                <select
                  value={daysBack}
                  onChange={e => setDaysBack(Number(e.target.value))}
                  aria-label='Time period'
                  className='px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hexagon'
                >
                  <option value={1}>Last 24 hours</option>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                </select>
              </div>
            </div>
            <div className='space-y-2'>
              {filteredLogs.length === 0 ? (
                <div className='text-center py-8 text-gray-500'>
                  No sync logs found
                </div>
              ) : (
                filteredLogs.map(log => (
                  <div
                    key={log.id}
                    className='border border-gray-200 rounded-lg p-4 hover:bg-gray-50'
                  >
                    <div className='flex items-center justify-between'>
                      <div className='flex-1'>
                        <div className='flex items-center space-x-3 mb-2'>
                          <span
                            className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              log.status
                            )}`}
                          >
                            {getStatusIcon(log.status)}
                            <span className='capitalize'>{log.status}</span>
                          </span>
                          <span className='text-xs text-gray-500'>
                            {log.sync_type}
                          </span>
                          {log.sync_frequency && (
                            <span className='text-xs text-gray-500'>
                              • {log.sync_frequency}
                            </span>
                          )}
                        </div>
                        <div className='text-sm font-medium text-gray-900'>
                          {log.exercises?.title || 'Unknown Exercise'}
                        </div>
                        <div className='text-xs text-gray-500 mt-1'>
                          {log.exercise_sources?.github_repo_path}
                          {log.version_created && (
                            <span className='ml-2'>
                              • Version: {log.version_created}
                            </span>
                          )}
                          {log.github_commit_hash && (
                            <span className='ml-2 font-mono'>
                              • {log.github_commit_hash.substring(0, 8)}
                            </span>
                          )}
                        </div>
                        {log.error_message && (
                          <div className='text-xs text-red-600 mt-1'>
                            Error: {log.error_message}
                          </div>
                        )}
                      </div>
                      <div className='text-right text-xs text-gray-500'>
                        <div>{formatDate(log.completed_at)}</div>
                        <div className='mt-1'>
                          {formatDuration(log.duration_ms)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default SyncDashboard;
